import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useIntl from 'useIntl';
import useOutsideClick from 'useOutsideClick';
import { TextField, Label, Button, forwardRef } from 'mw-style-react';
import { RECENT, SET_MODAL } from 'constants';
import {
  GET_DEFAULT_ACTOR_TPL,
  GET_EDGE_TYPES,
  SEARCH_ACTORS,
} from '@control-front-end/common/constants/graphActors';

import ActorAvatar from '../ActorAvatar';
import mes from './intl';
import './ActorsPopup.scss';

const ForwardTextField = forwardRef(TextField);

/**
 * Поиск акторов
 */
function ActorsPopup(props) {
  const {
    handleSelect,
    handleBlur,
    exclude = [],
    excludeFormIds = [],
    size,
  } = props;
  const t = useIntl();
  const popupRef = useRef();
  const dispatch = useDispatch();
  const systemForms = useSelector((state) => state.systemForms);
  const [actors, setActors] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(false);
  const reactionsFormId = systemForms.reactions.id;
  excludeFormIds.push(reactionsFormId);

  const clearSearch = () => {
    setQuery('');
    setFocus(false);
  };

  const handleSearch = ({ value: newQuery }) => {
    setQuery(newQuery);
    if (newQuery.length < 2) {
      setActors([]);
      return;
    }
    dispatch({
      type: SEARCH_ACTORS.REQUEST,
      payload: { query: newQuery, localState: true },
      callback: (result) => {
        setActors(result);
        setFocus(true);
      },
    });
  };

  // Загрузить рекомендации по акторам (исключая системных)
  const loadSuggestions = () => {
    dispatch({
      type: RECENT.REQUEST,
      payload: { objTypes: 'actor', customActor: true },
      callback: (data) => {
        setSuggestions(data);
      },
    });
  };

  useEffect(() => {
    loadSuggestions();
    dispatch({ type: GET_EDGE_TYPES.REQUEST });
  }, []);
  useOutsideClick({
    ref: popupRef,
    callback: handleBlur,
  });

  /**
   * Создать новый актор с заполнением модального окна
   */
  const handleCreateActorModal = () => {
    dispatch({
      type: GET_DEFAULT_ACTOR_TPL.REQUEST,
      callback: () => {
        dispatch({
          type: SET_MODAL,
          payload: {
            name: 'CreateActor',
            data: {},
            closeConfirm: true,
            callback: (actor) => handleSelect(actor.id, actor),
          },
        });
      },
    });
  };

  const handleListItemClick = (actorModel) => {
    setQuery('');
    setFocus(false);
    handleSelect(actorModel.id, actorModel);
    if (handleBlur) handleBlur();
  };

  const renderItems = (list, title) => {
    const filterList = list.filter((i) => !excludeFormIds.includes(i.formId));
    const count = filterList.length;
    if (!count) return null;
    const items = filterList.map((i) => {
      return (
        <div
          key={i.id}
          styleName="sa__result__item"
          onClick={() => handleListItemClick(i)}
        >
          <ActorAvatar
            pictureUrl={i.pictureUrl}
            color={i.color}
            colors={i.allColors}
            accessDenied={i.accessDenied}
            starred={i.layerStarred}
            status={i.status}
            colorFilled={true}
          />
          <div styleName="info">
            <Label styleName="sa__result__item__title" value={i.title} />
            <Label styleName="template" fontSize="small" value={i.formTitle} />
          </div>
        </div>
      );
    });
    return (
      <>
        <div styleName="sa__result__header">
          <Label value={`${title}: ${count}`} fontSize="semibold" />
        </div>
        <div styleName="sa__result__content">{items}</div>
      </>
    );
  };

  const renderSearchResult = () => {
    if (!focus) return null;
    const isSearch = query.trim().length > 1;
    const filtered = actors.filter((i) => !exclude.includes(i.id));
    const filteredS = suggestions.filter((i) => !exclude.includes(i.id));
    const resultList = isSearch ? filtered : filteredS;
    const label = isSearch ? t(mes.searchResults) : t(mes.suggestions);

    return (
      <div styleName="sa__result">
        {resultList.length ? renderItems(resultList, label) : null}
      </div>
    );
  };

  return (
    <div ref={popupRef} styleName="sa">
      <ForwardTextField
        styleName="sa__field"
        size={size}
        value={query}
        bordered={true}
        autoFocus={true}
        rightIcon={query.length ? 'close' : ''}
        onClickRightIcon={clearSearch}
        placeholder={t(mes.searchActors)}
        onFocus={() => setFocus(true)}
        onChange={handleSearch}
      />
      <Button
        styleName="sa__btn"
        fontWeight="normal"
        label={t(mes.create)}
        onClick={handleCreateActorModal}
      />
      {renderSearchResult()}
    </div>
  );
}

ActorsPopup.propTypes = {
  handleSelect: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  exclude: PropTypes.array,
  size: PropTypes.oneOf(['medium', 'large']),
};

export default ActorsPopup;
